import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import {
  defaultFaqsQueryOptions,
  putHiddenDefaultFaqs,
  type DefaultFaqItem,
  type DefaultFaqsQueryContext,
} from './default-faqs-queries';

export type DefaultFaqsContext = DefaultFaqsQueryContext;

/**
 * EU-641 — estado do painel "FAQs padrão" nos fluxos com registro EXISTENTE
 * (editar vaga/programa e criar vaga — persistência IMEDIATA por alternância,
 * FQC-012). Toggle otimista: aplica no cache, manda o replace-set completo ao
 * BFF e, em falha, REVERTE o cache + toast (FQC-013). A criação de programa
 * NÃO usa este hook (escolhas em memória até o POST — FQC-014/015).
 */
export function useDefaultFaqsPanel(context: DefaultFaqsContext, opts?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  const options = defaultFaqsQueryOptions(context, opts?.enabled ?? true);
  const query = useQuery(options);

  function toggle(faqId: number, hidden: boolean) {
    const key = options.queryKey;
    const prev = queryClient.getQueryData<{ items: DefaultFaqItem[] }>(key);
    if (!prev) return;
    const nextItems = prev.items.map((item) => (item.id === faqId ? { ...item, hidden } : item));
    queryClient.setQueryData(key, { items: nextItems });
    const hiddenIds = nextItems.filter((item) => item.hidden).map((item) => item.id);
    void putHiddenDefaultFaqs(context, hiddenIds).catch(() => {
      // FQC-013 — o switch volta ao estado anterior e o consultor é avisado.
      queryClient.setQueryData(key, prev);
      toast.error('Não foi possível salvar a alteração. Tente novamente.');
    });
  }

  return {
    items: query.data?.items ?? [],
    loading: query.isLoading,
    error: query.isError,
    refetch: () => void query.refetch(),
    toggle,
  };
}
